import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useRef } from 'react';
import { useFullscreen } from '../../hooks/useFullscreen';
import { GridHeaderDay } from './GridHeaderDay/GridHeaderDay';
import { GridRows } from './GridRows/GridRows';
import { GridSpaceCell } from './GridSpaceCell/GridSpaceCell';
import styles from './TimetableGrid.module.scss';
import { useTimetableStore } from './TimetableProvider';

export const TimetableGrid: FC = observer(() => {
  const isFullscreen = useFullscreen();

  const pinboardStore = useTimetableStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const cardSearchString = pinboardStore.cardSearchString;
  const filterFunctionClasses = pinboardStore.filterFunctionClasses;
  const showConflictsHighlight = pinboardStore.showConflictsHighlight;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const search = cardSearchString.toLowerCase();
    const cards = container.querySelectorAll<HTMLElement>('[data-search-text]');
    cards.forEach((el) => {
      const matchesSearch = !search || (el.dataset.searchText ?? '').includes(search);
      const matchesFilter = !filterFunctionClasses || el.dataset.functionCard === 'true';
      const matchesConflict = !showConflictsHighlight || el.hasAttribute('data-has-conflict');
      const wrapper = el.parentElement;
      if (wrapper) {
        wrapper.style.display = matchesFilter ? '' : 'none';
      }
      el.style.opacity = matchesSearch && matchesConflict ? '' : '0.2';
      el.style.filter = matchesSearch && matchesConflict ? '' : 'grayscale(1)';
    });
  }, [cardSearchString, filterFunctionClasses, showConflictsHighlight]);

  // P1-7: scroll the first rendered candidate row into view when a finding is highlighted, then clear.
  const scrollToRows = pinboardStore.scrollToRows;
  useEffect(() => {
    const container = containerRef.current;
    if (!container || scrollToRows.length === 0) return;
    for (const rowUuid of scrollToRows) {
      const el = container.querySelector(`[data-row-uuid="${rowUuid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      }
    }
    pinboardStore.clearScrollToRows();
  }, [scrollToRows, pinboardStore]);

  const cellWidth = pinboardStore.showPause
    ? `${100 / pinboardStore.hours.length}%`
    : `${100 / pinboardStore.hours.filter((h) => !h.isPause).length}%`;

  const daysWidth = `${100 / pinboardStore.days.length}%`;

  const classes = classNames(styles['timetable-grid'], {
    [styles['is-fullscreen']]: isFullscreen,
    [styles['control-mode']]: pinboardStore.controlMode.isActive,
  });

  return (
    <div
      ref={containerRef}
      className={classes}
      // Markierung für den Outside-Click-Handler (CardPreview): Klicks ins Board legen die
      // Karte nicht zurück (Zellen platzieren selbst, sonstige Board-Klicks tun nichts).
      data-timetable-board=''
      data-color-by-subject={pinboardStore.cardColorBySubject ? '' : undefined}
      data-no-icons={pinboardStore.highlightMode.includes('noIcons') ? '' : undefined}
      data-highlight-rooms={pinboardStore.showRoomsHighlight ? '' : undefined}
      data-highlight-warnings={pinboardStore.showWarningsHighlight ? '' : undefined}
      data-picking={pinboardStore.pickedCard ? '' : undefined}
    >
      <div className={styles.header}>
        <GridSpaceCell />
        {pinboardStore.days.map((day) => {
          return <GridHeaderDay key={day.value} cellWidth={cellWidth} day={day} daysWidth={daysWidth} />;
        })}
      </div>
      <GridRows cellWidth={cellWidth} daysWidth={daysWidth} />
    </div>
  );
});
